import React from "react";
import { Card, CardContent } from "./ui/card";
import { MapPin, Users, Heart, Shield, Clock, Home } from "lucide-react";

export const About = () => {
  const features = [
    {
      icon: <MapPin className="h-6 w-6" />,
      title: "Prime Location",
      description: "21 Barnston Lane, Moreton, Wirral, CH46 7TN"
    },
    {
      icon: <Home className="h-6 w-6" />,
      title: "Purpose-Built",
      description: "Modern nursing home designed for comfort and accessibility"
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Approximately 30 Residents",
      description: "Intimate community for personalised attention"
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "24-Hour Care",
      description: "Professional care team available around the clock"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Safe & Secure",
      description: "Secure environment with comprehensive safety measures"
    },
    {
      icon: <Heart className="h-6 w-6" />,
      title: "Person-Centred",
      description: "Individual care plans tailored to each resident"
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            About Barnston Court
          </h2>
          <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A welcoming nursing home in the heart of Moreton, now proudly part of the Cozee Care family
          </p>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          {/* Text Content */}
          <div className="space-y-6">
            <div className="prose prose-lg">
              <p className="text-gray-700 leading-relaxed">
                Barnston Court is a purpose-built nursing home providing high-quality residential, 
                nursing, and dementia care for approximately 30 residents in a safe, secure, 
                and welcoming environment.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our dedicated 24-hour professional care team works closely with residents and 
                their families to create personalised care plans that respect individual needs, 
                preferences, and dignity.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We maintain strong partnerships with local healthcare professionals, GPs, and 
                specialists to ensure comprehensive support for every resident's wellbeing.
              </p>
            </div>

            {/* Transition Message Card */}
            <Card className="border-l-4 border-l-orange-500 bg-teal-50">
              <CardContent className="pt-6">
                <h3 className="font-bold text-teal-900 mb-3 text-lg">A New Chapter with Cozee Care</h3>
                <p className="text-gray-700">
                  Barnston Court is now part of the Cozee Care family. While ownership has changed, 
                  our commitment to high-quality, person-centred care remains stronger than ever. 
                  We're excited to bring enhanced resources and support to our residents and their families.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Video */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
              <video
                src="https://customer-assets.emergentagent.com/job_81c4535c-71eb-4a2b-bbef-6e28bbec7a64/artifacts/ila982pb_Video%20Project%201.mp4"
                className="w-full h-full object-cover"
                controls
                playsInline
                controlsList="nofullscreen"
                disablePictureInPicture
                poster="https://customer-assets.emergentagent.com/job_81c4535c-71eb-4a2b-bbef-6e28bbec7a64/artifacts/5s8rb7kd_DSC_0096-1024x683.jpg"
              >
                Your browser does not support the video tag.
              </video>
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-orange-100 rounded-2xl -z-10"></div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card
              key={index}
              className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white"
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="bg-teal-100 text-teal-700 p-3 rounded-lg flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
